import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {GeometryAttributeData, GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {NumberLike} from "#template/nodes/types"
import {numberLike} from "#template/nodes/number/fundamental"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type SplitOperatorParameters<TPrimary, TAttributeSet> = {
    name: string
    primaryAttribute: TPrimary | null
    attributeSet: TAttributeSet | null
    count: NumberLike | null
}

export const splitOperatorParametersSchema = <TPrimary, TAttributeSet>(
    primarySchema: z.ZodMiniType<TPrimary, TPrimary>,
    attributeSetSchema: z.ZodMiniType<TAttributeSet, TAttributeSet>,
) =>
    z.object({
        name: z.string(),
        primaryAttribute: z.nullable(primarySchema),
        attributeSet: z.nullable(attributeSetSchema),
        count: z.nullable(numberLike),
    })

function applySplitOperator<T extends IndexingDim>(
    primaryAttribute: GeometryAttributeData<T>,
    attributeSet: GeometryAttributeSetData<T>,
    count: number,
): GeometryAttributeSetData<T> {
    const primaryAttrRefData = primaryAttribute.ref
    const attrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSet)

    const splitAttrRefDataSet = applyGeometryOperator(
        (primaryAttrRef: AttributeRef, attrRefSet: AttributeRefSet, count: number) => {
            const splitAttrRefList = Operators.split(primaryAttrRef, attrRefSet, count)

            const splitAttrRefSet: AttributeRefSet = {}
            splitAttrRefList.forEach((attrRef, index) => {
                splitAttrRefSet[index.toString()] = attrRef
            })
            return splitAttrRefSet
        },
        primaryAttrRefData,
        attrRefDataSet,
        count,
    )

    return geometryAttributeRefDataSetToGeometryAttributeSetData(splitAttrRefDataSet, attributeSet.dim)
}

export abstract class GeometrySplitOperatorNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
    out TAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeSetNode<T, SplitOperatorParameters<TAttribute, TAttributeSet>> {
    protected override hasValidParameters(
        parameters: SplitOperatorParameters<TAttribute, TAttributeSet>,
    ): parameters is SplitOperatorParameters<TAttribute, TAttributeSet> & NonNullableParameters<SplitOperatorParameters<TAttribute, TAttributeSet>> {
        return parameters.primaryAttribute !== null && parameters.attributeSet !== null && parameters.count !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<SplitOperatorParameters<TAttribute, TAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {primaryAttribute, attributeSet, count} = parameters

        const [primaryAttributeDataValid, primaryAttributeDataInvalid] = scope.branch(evaluator.evaluate(primaryAttribute))
        const [attributeSetDataValid, attributeSetDataInvalid] = scope.branch(evaluator.evaluate(attributeSet))
        const [countValid, countInvalid] = scope.branch(evaluator.evaluate(count))

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(primaryAttributeDataValid, attributeSetDataValid, countValid),
            ([primaryAttributeData, attributeSetData, count]) => applySplitOperator(primaryAttributeData, attributeSetData, count),
            "applySplitOperator",
        )

        return {
            evaluatedGeometryData: geometryAttributeSetData,
            invalidator: scope.phi(primaryAttributeDataInvalid, attributeSetDataInvalid, countInvalid),
        }
    }
}
